import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CurrencyDollarIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XIcon
} from '@heroicons/react/outline';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';
import ImageGallery from '../components/ImageGallery';

const QuoteResponses = () => {
  const { rfqId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load RFQ details
        let rfqData = location.state?.rfq;
        if (!rfqData) {
          const rfqDoc = await getDoc(doc(db, 'posts', rfqId));
          if (rfqDoc.exists()) {
            rfqData = { id: rfqDoc.id, ...rfqDoc.data() };
          } else {
            setError('RFQ not found');
            return;
          }
        }
        setRfq(rfqData);

        // Load quotes
        const quotesQuery = query(
          collection(db, 'replies'),
          where('postId', '==', rfqId),
          orderBy('timestamp', 'desc')
        );
        
        const quotesSnapshot = await getDocs(quotesQuery);
        const quotesData = quotesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setQuotes(quotesData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [rfqId, location.state]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date not specified';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-CC5500"></div>
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">RFQ Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The RFQ you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/market-feed')}
            className="px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Back to Market Feed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/rfq/${rfq.id}`, { state: { rfq } })}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Quote Responses
                </h1>
                <p className="text-gray-600">
                  {quotes.length} response{quotes.length !== 1 ? 's' : ''} received
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* RFQ Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-CC5500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Request for Quote</h3>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">
              {rfq.textContent || rfq.title || 'RFQ Title'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Budget: {rfq.budget ? formatCurrency(rfq.budget) : 'Not specified'}</span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Posted: {formatDate(rfq.timestamp)}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600">Category: {(rfq.categories || [rfq.channel || rfq.category || 'General']).join(', ')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quotes List */}
        <div className="space-y-6">
          {quotes.length > 0 ? (
            quotes.map((quote) => (
              <div key={quote.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  {/* Quote Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-CC5500 rounded-full flex items-center justify-center mr-3">
                        <UserIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {quote.authorName || 'Anonymous'}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {formatDate(quote.timestamp)}
                        </p>
                      </div>
                    </div>
                    {quote.offerPrice && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Quote Amount</p>
                        <p className="text-lg font-bold text-CC5500">
                          {formatCurrency(quote.offerPrice)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Quote Details */}
                  {quote.textContent && (
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900 mb-2">Proposal Details</h5>
                      <p className="text-gray-700 leading-relaxed">
                        {quote.textContent}
                      </p>
                    </div>
                  )}

                  {/* Quote Images */}
                  {quote.media && quote.media.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900 mb-2">Supporting Documents</h5>
                      <ImageGallery images={quote.media} />
                    </div>
                  )}

                  {/* Quote Terms */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {quote.deliveryTime && (
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-600">Delivery: {quote.deliveryTime}</span>
                      </div>
                    )}
                    {quote.warranty && (
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-600">Warranty: {quote.warranty}</span>
                      </div>
                    )}
                    {quote.terms && (
                      <div className="md:col-span-3">
                        <span className="text-gray-600">Terms: {quote.terms}</span>
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  {quote.authorEmail && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-gray-600">
                        Contact: <span className="font-medium">{quote.authorEmail}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Quotes Yet</h3>
              <p className="text-gray-600 mb-4">
                No suppliers have submitted quotes for this RFQ yet.
              </p>
              <button
                onClick={() => navigate('/market-feed')}
                className="px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Browse More RFQs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteResponses; 